import {
  registerItemSource,
  tooltipText,
  type AccessibleItems,
  type AccessibleNode,
} from "../core/accessibility.js";
import type { Rect } from "../../common/geometry.js";
import { visibleRowRange, type RowRange } from "../core/virtualWindow.js";
import { cellText, type DataGridColumn } from "../controls/DataGridColumn.js";
import { DataGrid } from "../controls/DataGrid.js";
import { ListBox } from "../controls/ListBox.js";
import { ListView } from "../controls/ListView.js";
import { MenuBar } from "../controls/MenuBar.js";
import { MenuPopup } from "../controls/MenuPopup.js";
import { NavigationIndex } from "../controls/NavigationIndex.js";
import { hasSubmenu, isSeparator, parseMnemonic, type MenuItemSpec } from "../controls/MenuItem.js";
import type { ScrollableBase } from "../controls/ScrollableBase.js";
import { StatusBar } from "../controls/StatusBar.js";
import { TabControl } from "../controls/TabControl.js";
import { Toolbar } from "../controls/Toolbar.js";
import { flattenTree, treeItemPositions } from "../controls/TreeNode.js";
import { TreeView } from "../controls/TreeView.js";

/**
 * The rows the data controls draw for themselves.
 *
 * Roughly half the words in a Webwin application are not on a control at all.
 * They are in `MenuItemSpec[]`, `TreeNodeSpec[]`, `ToolbarItemSpec[]`, grid
 * rows and list items - plain data, painted directly, with no child control
 * per row to describe. Without this the mirror can say "a list" and not what
 * is in it.
 *
 * Every one of these reports the whole `count` and materialises only the
 * window on screen. `aria-setsize` and `aria-posinset` were designed for
 * exactly that, and are why a reader can truthfully say "3 of 100000" from
 * thirty elements.
 */
let registered = false;

/** Idempotent, and called only when a desktop turns accessibility on. */
export function registerAccessibleItemSources(): void {
  if (registered) return;
  registered = true;

  registerItemSource(ListBox, listBoxItems);
  registerItemSource(ListView, listViewItems);
  registerItemSource(DataGrid, dataGridItems);
  registerItemSource(TreeView, treeViewItems);
  registerItemSource(MenuBar, menuBarItems);
  registerItemSource(MenuPopup, menuPopupItems);
  registerItemSource(Toolbar, toolbarItems);
  registerItemSource(TabControl, tabControlItems);
  registerItemSource(StatusBar, statusBarItems);
  registerItemSource(NavigationIndex, navigationIndexItems);
}

/** Exposed for a test that wants the registry rebuilt. */
export function resetAccessibleItemSources(): void {
  registered = false;
}

// --- geometry -------------------------------------------------------------

/**
 * A row's rectangle on the canvas.
 *
 * `itemContentRect` and friends answer in content coordinates - before the
 * scroll, relative to the viewport - so the scroll and the control's own
 * place in the tree both still have to be applied. Worth the arithmetic: a
 * box in the right place is what lets a phone read the row under a finger,
 * and explore-by-touch is how TalkBack is mostly used.
 */
function rowBounds(control: ScrollableBase, content: Rect): Rect {
  const view = control.viewportRect;
  const origin = control.toScreen({
    x: view.x - control.scrollOffsetX + content.x,
    y: view.y - control.scrollOffsetY + content.y,
  });
  const scale = control.screenScale;
  return {
    x: origin.x,
    y: origin.y,
    width: content.width * scale,
    height: content.height * scale,
  };
}

/** The window of rows a scrollable control is currently painting. */
function scrolledWindow(
  control: ScrollableBase,
  rowHeight: number,
  count: number,
  padStart: number,
  extentAdjust = 0,
): RowRange {
  const view = control.viewportRect;
  // Overscan, so a scroll of one pixel does not rebuild the edges every frame.
  return visibleRowRange(
    control.scrollOffsetY,
    view.height - extentAdjust,
    rowHeight,
    padStart,
    count,
    OVERSCAN,
  );
}

const OVERSCAN = 5;

const listPadding = (control: ScrollableBase): number =>
  control.desktop?.theme.metrics.listPadding.top ?? 0;

// --- lists ----------------------------------------------------------------

function listBoxItems(control: unknown): AccessibleItems {
  const list = control as ListBox<unknown>;
  const theme = list.desktop?.theme ?? null;
  const count = list.count;
  return {
    count,
    range: scrolledWindow(list, list.itemHeight, count, listPadding(list)),
    item: (i): AccessibleNode => ({
      role: "option",
      name: list.itemText(list.items[i], i),
      selected: list.isSelected(i),
      posInSet: i,
      setSize: count,
    }),
    itemBounds: (i) => (theme ? rowBounds(list, list.itemContentRect(i, theme)) : null),
  };
}

function listViewItems(control: unknown): AccessibleItems {
  const view = control as ListView<unknown>;
  const count = view.count;
  const columns = view.columns;
  return {
    count,
    range: scrolledWindow(view, view.rowHeight, count, 0),
    item: (i): AccessibleNode => {
      const item = view.items[i] as never;
      // One row, one string. A cell per column would be a `row` of
      // `gridcell`s, which is right for a grid somebody navigates cell by
      // cell and wrong for a report they read line by line.
      const text = columns.length
        ? columns.map((c, n) => view.cellText(item, n, c)).join(", ")
        : String(item);
      return {
        role: "option",
        name: text,
        selected: view.isSelected(i),
        posInSet: i,
        setSize: count,
      };
    },
  };
}

// --- grid -----------------------------------------------------------------

function dataGridItems(control: unknown): AccessibleItems {
  const grid = control as DataGrid<never>;
  const columns = grid.columns as DataGridColumn<never>[];
  const rows = grid.rowCount;
  const window = scrolledWindow(grid, grid.rowHeight, rows, 0, grid.headerHeight);
  const current = grid.currentCell;

  return {
    count: rows,
    range: window,
    item: (i): AccessibleNode | null => {
      const row = grid.rowAt(i);
      if (row === undefined) return null;
      return {
        // Not `role="grid"` with `role="row"`, tempting as the names are.
        // A row must contain `gridcell`s to be a valid row, and the item
        // model here is flat - one node per index, with nowhere to hang a
        // cell. An invalid grid is read worse than a valid list, so the row
        // is one option carrying the whole line. Cell-by-cell navigation is
        // a real gap and is written down as one.
        role: "option",
        name: columns.map((c) => cellText(c, row)).join(", "),
        selected: i === current.row,
        setSize: rows,
        posInSet: i,
      };
    },
  };
}

// --- tree -----------------------------------------------------------------

function treeViewItems(control: unknown): AccessibleItems {
  const tree = control as TreeView<never>;
  const theme = tree.desktop?.theme ?? null;
  const rows = flattenTree(tree.nodes);
  const positions = treeItemPositions(rows);
  const count = rows.length;

  return {
    count,
    range: scrolledWindow(tree, tree.itemHeight, count, listPadding(tree)),
    item: (i): AccessibleNode | null => {
      const row = rows[i];
      const at = positions[i];
      if (!row || !at) return null;
      return {
        role: "treeitem",
        name: row.node.text,
        // Only when it can actually be opened. `aria-expanded="false"` on a
        // leaf tells a reader there is something inside to go and find.
        ...(row.expandable ? { expanded: row.node.expanded === true } : {}),
        ...(tree.checkBoxes ? { checked: row.node.checked === true } : {}),
        selected: tree.isSelected(row.node),
        disabled: row.node.enabled === false,
        level: at.level,
        posInSet: at.posInSet,
        setSize: at.setSize,
      };
    },
    itemBounds: (i) => (theme ? rowBounds(tree, tree.rowContentRect(i, theme)) : null),
  };
}

// --- menus and bars -------------------------------------------------------

/**
 * One menu row.
 *
 * The text goes through `parseMnemonic` and never straight out of the spec.
 * "&File" read literally is "ampersand File", and "Save && Exit" is worse.
 * The marker it strips becomes `aria-keyshortcuts`, so the shortcut is
 * announced as a shortcut rather than lost.
 */
function menuItemNode(item: MenuItemSpec, index: number, count: number, alt: boolean): AccessibleNode {
  if (isSeparator(item)) return { role: "separator", name: "", posInSet: index, setSize: count };
  const label = parseMnemonic(item.text ?? "");
  const shortcut =
    label.mnemonic ? `${alt ? "Alt+" : ""}${label.mnemonic.toUpperCase()}` : undefined;
  // The shortcut and what the row does are both things a sighted user reads off it.
  const description = [item.shortcut, item.tooltip ? tooltipText(item.tooltip) : ""].filter(Boolean).join(". ");
  const role =
    item.checked === undefined ? "menuitem"
    : item.radio ? "menuitemradio"
    : "menuitemcheckbox";
  return {
    role,
    name: label.text,
    ...(item.checked === undefined ? {} : { checked: item.checked }),
    ...(hasSubmenu(item) ? { expanded: false } : {}),
    disabled: item.enabled === false,
    ...(shortcut ? { keyShortcuts: shortcut } : {}),
    ...(description ? { description } : {}),
    posInSet: index,
    setSize: count,
  };
}

function menuBarItems(control: unknown): AccessibleItems {
  const bar = control as MenuBar;
  const items = bar.items;
  return {
    count: items.length,
    // A menu bar has as many rows as it has menus, and they are all on screen.
    range: { first: 0, last: items.length - 1 },
    item: (i): AccessibleNode | null => {
      const item = items[i];
      if (!item) return null;
      const node = menuItemNode(item, i, items.length, true);
      return bar.openIndex === i ? { ...node, expanded: true } : node;
    },
  };
}

function menuPopupItems(control: unknown): AccessibleItems {
  const popup = control as MenuPopup;
  const items = popup.items;
  return {
    count: items.length,
    range: { first: 0, last: items.length - 1 },
    item: (i): AccessibleNode | null => {
      const item = items[i];
      if (!item) return null;
      const node = menuItemNode(item, i, items.length, false);
      return popup.highlightIndex === i ? { ...node, selected: true } : node;
    },
  };
}

function toolbarItems(control: unknown): AccessibleItems {
  const bar = control as Toolbar;
  const items = bar.items;
  return {
    count: items.length,
    range: { first: 0, last: items.length - 1 },
    item: (i): AccessibleNode | null => {
      const item = items[i];
      if (!item) return null;
      if (item.separator) return { role: "separator", name: "" };
      const label = parseMnemonic(item.text ?? "");
      return {
        role: "button",
        // Caption first, tooltip only when there is none - a toolbar in icon
        // mode has no caption at all, and `tooltip` is already the field that
        // names those buttons for everybody else. Preferring the tooltip
        // outright made one bar read "Back", "Go up one level (Alt+Up)",
        // "Cut the selection to the clipboard": three registers in a row.
        name: label.text || (item.tooltip ?? ""),
        ...(label.text && item.tooltip ? { description: item.tooltip } : {}),
        ...(item.toggle ? { checked: item.checked === true } : {}),
        disabled: item.enabled === false,
        ...(label.mnemonic ? { keyShortcuts: label.mnemonic.toUpperCase() } : {}),
        posInSet: i,
        setSize: items.length,
      };
    },
  };
}

function tabControlItems(control: unknown): AccessibleItems {
  const tabs = control as TabControl;
  const pages = tabs.pages;
  return {
    count: pages.length,
    range: { first: 0, last: pages.length - 1 },
    item: (i): AccessibleNode | null => {
      const page = pages[i];
      if (!page) return null;
      return {
        role: "tab",
        name: page.text,
        selected: tabs.selectedIndex === i,
        posInSet: i,
        setSize: pages.length,
      };
    },
  };
}

function navigationIndexItems(control: unknown): AccessibleItems {
  const index = control as NavigationIndex;
  const entries = index.entries;
  return {
    count: entries.length,
    range: { first: 0, last: entries.length - 1 },
    item: (i): AccessibleNode | null => {
      const entry = entries[i];
      if (!entry) return null;
      return {
        role: "option",
        name: entry.name,
        selected: i === index.highlightIndex,
        level: entry.depth,
        posInSet: i,
        setSize: entries.length,
      };
    },
  };
}

function statusBarItems(control: unknown): AccessibleItems {
  const bar = control as StatusBar;
  const panels = bar.panels;
  return {
    count: panels.length,
    range: { first: 0, last: panels.length - 1 },
    item: (i): AccessibleNode | null => {
      const panel = panels[i];
      if (!panel) return null;
      const text = panel.text ?? "";
      // A panel is a readout, not a control. Its words are the whole of it,
      // and putting them in as text is what lets Ctrl+F find them.
      if (!text) return null;
      return { role: "none", name: "", text };
    },
  };
}

export { tooltipText };
